import { createProjectOptions } from "../utils/utils.js";
import { fetchGeoserverResource, fetchResource, fetchTrackConvertorResource, fetchSensorIntersects } from "./fetchResource.js";

export async function getEPSGs() {
    try {
        return await fetchResource(`/epsg`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getCustomersData() {
    try {
        return await fetchResource(`/customers`)
    } catch (error) {
        throw new Error(error)
    }
}


export async function getCustomerRolesAPI(customerId) {
    try {
        return await fetchResource(`/customers/${customerId}/ownroles`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getCustomersLayerTypes(customerId) {
    try {
        return await fetchResource(`/customers/${customerId}/tracklayertypes`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getExternalGeoServiceTypes() {
    try {
        return await fetchResource(`/external-geo-services/types`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getExternalGeoServices(customerId) {
    try {
        return await fetchResource(`/external-geo-services/customers/${customerId}`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getProjectExternalGeoServices(projectId) {
    try {
        return await fetchResource(`/external-geo-services/projects/${projectId}`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function addExternalGeoServiceToProject(projectId, externalGeoServiceId) {
    try {
        return await fetchResource(`/external-geo-services/projects/${projectId}`, {
            method: "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ externalGeoServiceId }),
        })
    } catch (error) {
        throw new Error(error)
    }
}

export async function removeExternalGeoServiceFromProject(projectId, externalGeoServiceId) {
    try {
        return await fetchResource(`/external-geo-services/projects/${projectId}/${externalGeoServiceId}`, {
            method: "DELETE",
        })
    } catch (error) {
        throw new Error(error)
    }
}

export async function createExternalGeoService(customerId, data) {
    try {
        return await fetchResource(`/external-geo-services/customers/${customerId}`, {
            method: "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        })
    } catch (error) {
        throw new Error(error)
    }
}

export async function updateExternalGeoService(customerId, serviceId, data) {
    try {
        return await fetchResource(`/external-geo-services/customers/${customerId}/${serviceId}`, {
            method: "PUT",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        })
    } catch (error) {
        throw new Error(error)
    }
}

export async function deleteExternalGeoService(customerId, serviceId, force = false) {
    try {
        const suffix = force ? '?force=true' : ''
        return await fetchResource(`/external-geo-services/customers/${customerId}/${serviceId}${suffix}`, {
            method: "DELETE",
        })
    } catch (error) {
        throw error
    }
}

export async function uploadVectorLayer(customerId, file) {
    try {
        const formData = new FormData();
        formData.append('file', file);
        return await fetchResource(`/customers/${customerId}/vector-layers/upload`, {
            method: 'POST',
            body: formData,
        });
    } catch (error) {
        throw new Error(error);
    }
}

export async function getCustomersCredits(customerId) {
    try {
        return await fetchResource(`/customers/${customerId}/credits`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getFinanceData() {
    try {
        return await fetchResource(`/administration/balance`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getMonthlyFinanceData() {
    try {
        return await fetchResource(`/administration/negativemonthlytransactionspercustomer`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getDailyFinanceData() {
    try {
        return await fetchResource(`/administration/negativedailytransactionspercustomer`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function saveTransaction(payload) {
    try {
        const data = await fetchResource(`/administration/transaction`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: payload
        })
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function getTransactionTypes() {
    try {
        return await fetchResource(`/transactiontypes`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getRoles(activeCustomerId) {
    try {
        return await fetchResource(`/customers/${activeCustomerId}/roles`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getPermissions() {
    try {
        return await fetchResource(`/permissions`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function saveUserRoles(customerId, userEmail, roleIds) {
    try {
        const data = await fetchResource(`/customers/${customerId}/setuserroles`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                userEmail, roleIds
            }
        })
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function removeUserFromCustomer(customerId, userId) {
    try {
        return await fetchResource(`/customers/${customerId}/removeuser/${userId}`, {
            method: "DELETE",
        })
    } catch (error) {
        throw new Error(error)
    }
}

export async function sendUserInvitation(customerId, email) {
    try {
        return await fetchResource(`/invitations/send`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: { customerId, email },
        })
    } catch (error) {
        throw new Error(error)
    }
}

export async function addUserToCustomerByEmail(customerId, userEmail, provisional = false) {
    try {
        const data = await fetchResource(`/customers/${customerId}/addusertocustomer`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                userEmail,
                ...(provisional && { provisional: true })
            }
        })
        return data
    } catch (error) {
        throw error
    }
}

export async function updateCommunicationEmailAddress(communicationEmailAddress) {
    try {
        return await fetchResource(`/users/communicationemailaddress`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: { communicationEmailAddress }
        })
    } catch (error) {
        throw error
    }
}

export async function updateUserEmailPreferences(receiveUpdatesPerEmail) {
    try {
        return await fetchResource(`/users/preferences`, {
            method: "PATCH",
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                receive_updates_per_email: receiveUpdatesPerEmail,
                receive_notifications_per_email: null,
            }
        })
    } catch (error) {
        throw error
    }
}

export function getUserDetails() {
    return fetchResource("/getgrant")
}

export function getUserByEmail(email) {
    return fetchResource(`/users?preferred_username=${email}&username=${email}`)
}

export async function getLoggedinUser() {
    try {
        const data = await fetchResource(`/users/own`)
        return data
    } catch (error) {
        throw error.status === 500 ? new Error("API_NOT_REACHABLE") : new Error(error.status == 401 ? "TOKEN_EXPIRED" : error)
    }
}

export async function getAllProjects() {
    try {
        const data = await fetchResource(`/regions/manager`)
        let projects = data.reduce((acc, val) => {
            return acc.concat(val.projects);
        }, []);

        projects = projects.map(project => createProjectOptions(project))
        return projects
    } catch (error) {
        throw new Error(error)
    }

}

export async function getProcessTypes() {
    try {
        const data = await fetchResource(`/processtype`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getProcessStatus() {
    try {
        const data = await fetchResource(`/processstatus`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getProcessPolygons(projectId) {
    try {
        const data = await fetchResource(`/projects/${projectId}/processes/polygons`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getSystemLoad() {
    try {
        const data = await fetchResource(`/system-load`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getProjectExtent(projectId) {
    try {
        const data = await fetchResource(`/projects/${projectId}/extent`)
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function copyProcessToProject(processId, targetProjectId) {
    try {
        const data = await fetchResource(`/processes/${processId}/copy`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                targetProjectId
            }
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getTagsAPI(ProjectId) {
    try {
        const data = await fetchResource(`/tags/${ProjectId}`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function createProcessTagAPI(projectId, processId, tagValue) {
    try {
        const data = await fetchResource(`/processes/${processId}/createtag`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                tagValue: tagValue,
                projectId: projectId
            }
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function saveProjectPolygons(projectId, polygons) {
    try {
        const data = await fetchResource(`/projects/${projectId}/polygons`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: polygons
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function addProcessTagAPI(processId, tagId) {
    try {
        const data = await fetchResource(`/processes/${processId}/addtag/${tagId}`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getSensorTypes() {
    try {
        const data = await fetchResource(`/sensortypes`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getProcessSensors(processId, sensorTypes) {
    try {
        const data = await fetchResource(`/sensors/${processId}/bytype`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: sensorTypes

        }
        )
        return data
    } catch (error) {
        throw new Error(error)
    }

}


export async function getCustomerViewers(customerId) {
    try {
        const data = await fetchResource(`/customers/${customerId}/viewers`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getProjectDefaultViewer(projectId) {
    try {
        const data = await fetchResource(`/projects/${projectId}/defaultviewer`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function setProjectDefaultViewer(projectId, viewerId) {
    try {
        const data = await fetchResource(`/projects/${projectId}/defaultviewer/${viewerId}`, { method: "PUT" })
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function removeProcessTagAPI(processId, tagId) {
    try {
        const data = await fetchResource(`/processes/${processId}/removetag/${tagId}`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function renameSection(sectionId, SectionName) {
    try {
        const data = await fetchResource(`/sections/${sectionId}`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                sectionName: SectionName
            }
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function disableProcess(processId) {
    try {
        const data1 = await fetchResource(`/processstatus/DISABLED`)
        const payload = { processStatusId: data1 }
        const data = await fetchResource(`/processes/${processId}`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: { processStatusId: data1 }
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function deleteProcessAPI(processId) {
    try {
        const data = await fetchResource(`/uploader/removeprocess/${processId}`, {
            method: "DELETE",
            excludeBasePath: true
        })
        return data
    } catch (error) {
        throw new Error(error.status == 405 ? "NOT ALLOWED" : error)
    }

}

export async function renameProcess(processId, processDesc, processTitle) {
    try {
        const data = await fetchResource(`/processes/${processId}`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                processTitle: processTitle ? processTitle : undefined,
                processDesc: processDesc ? processDesc : undefined,
            }
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function changeProcessStatusNote(processId, processStatusNote) {
    try {
        const data = await fetchResource(`/processes/${processId}`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                processId,
                processStatusNote
            }
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function reparseSensorsByProcess(processId) {
    try {
        const data = await fetchResource(`/processes/${processId}/reparse-sensors`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
        })


        if (!data.success) {
            console.log(data)
            throw new Error(`Error ${data.error?.message}`,)
        }
        return data
    } catch (error) {
        throw new Error(error)
    }

}


export async function changeVideoProcess(payloadData) {
    try {
        const data = await fetchResource(`/uploader/ignite/videos/final`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: payloadData,
            excludeBasePath: true
        })


        if (!data.success) {
            console.log(data)
            throw new Error(`Error ${data.error?.message}`,)
        }
        return data
    } catch (error) {
        throw new Error(error)
    }

}


export async function updateProcess(processId, payload) {
    try {
        const data = await fetchResource(`/processes/${processId}`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: payload
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function updateProcessBulk(payload) {
    try {
        const data = await fetchResource(`/processes/access`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: payload
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}


export async function addUserToProject(projectId, uId) {
    try {
        let userId;
        if (!uId) {
            const user = await getLoggedinUser()
            if (!user) throw new Error("No User")
            userId = user.userId
        } else {
            userId = uId
        }

        const data = await fetchResource(`/users/${userId}/addproject/${projectId}`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            }

        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function removeUserFromProject(projectId, userId) {
    try {
        const data = await fetchResource(`/users/${userId}/removeproject/${projectId}`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            }
        })
        return data
    } catch (error) {
        throw new Error(error)
    }
}


export async function generateShareableLink(config) {
    try {

        const data = await fetchResource(`/configshares`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: {
                config
            }
        })
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getDataFromSharedLink(linkId) {
    try {

        const data = await fetchResource(`/configshares/${linkId}`)
        return data
    } catch (error) {
        throw new Error(error)
    }

}

export async function getSensorData(processId, newPanos = false) {
    try {
        let url = `/sensors/${processId}?format=compact`
        if (newPanos) {
            url += "&lowestresolution=true"
        }
        const data = await fetchResource(url)
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function getSensorsByType(processId, type) {
    try {
        const data = await fetchResource(`/sensors/bytype/${processId}?type=${type}`)
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function getSensorsByMisc(processId, misc) {
    try {
        const data = await fetchResource(`/sensors/bymisc/${processId}?misc=${misc}`)
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function getDetectionData(detectionId) {
    try {
        const data = await fetchResource(`/detections/images?detectionId=${detectionId}&threshold=0.7`)
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function getSensorIntersectData(projectId, process) {
    try {
        // const coordquery = `intersects/${projectId}?x=${process.coord_wgs84[0]}&y=${process.coord_wgs84[1]}`
        // const data = await fetchResource(`/sensors/${coordquery}`)
        const data = await fetchSensorIntersects(process.coord_wgs84[0], process.coord_wgs84[1], process.activeProcesses ?? [])
        return data
    } catch (error) {
        console.log(error, "errr")
        throw new Error(error)
    }
}

export async function getTrajectoryData(processId) {
    try {
        const data = await fetchResource(`/sensors/${processId}?type=7`)
        return data
    } catch (error) {
        throw new Error(error)
    }
}


export async function addUserToCustomer(userEmail, customerId) {
    try {
        const user = await getUserByEmail(userEmail)
        if (!user) throw new Error("No User")
        const data = await fetchResource(`/customers/${customerId}/add/${user.userId}`)
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function getUserProjectsById(userId, customerId) {
    try {
        const data = await fetchResource(`/customers/${customerId}/users/${userId}/projects`)
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function getProjectDefaultConfigsAPI(projectId) {
    try {
        const data = await fetchResource(`/projects/${projectId}`)
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function updateProjectDefaultSettingsAPI(projectId, configs) {
    try {
        const data = await fetchResource(`/projects/${projectId}`,
            {
                method: "PATCH",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ projectId, x2bConfig: configs }),
            }
        )
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function getProjectProcesses(projectId) {
    try {
        const data = await fetchResource(`/projects/${projectId}/processes`)
        return data
    } catch (error) {
        throw new Error(error)
    }
}

// export async function getProject(projectId) {
//     try {
//         return await fetchResource(`/projects/${projectId}/processes`)
//     } catch (error) {
//         throw new Error(error)
//     }
// }

export async function createProject(projectData) {
    try {

        const data = await fetchResource(`/projects`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(projectData)

        })
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function createSupportTicket(userEmail, message, files) {
    try {
        const formData = new FormData()
        formData.append('IsHtml', true)
        formData.append('Subject', "Support Request")
        formData.append('Body', message)
        formData.append('Recipients', "x2bim-support@infraview.net")
        formData.append('userEmail', userEmail)
        files.forEach(row => {
            const logoFile = new File([row], row.name, { type: row.type })
            formData.append('Attachments', logoFile)
        })

        return fetchResource(`/support/sendemail`, {
            method: 'POST',
            body: formData
        });
    } catch (error) {
        throw new Error(error)
    }
}

export async function updateProject(projectId, projectData) {
    try {
        const data = await fetchResource(`/projects/${projectId}`, {
            method: "PATCH",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(projectData)
        })
        return data
    } catch (error) {
        throw new Error(error)
    }
}

export async function getUserAccess(projectId) {
    try {
        const projectAccess = await fetchResource(`/projects/${projectId}/projectaccess`)
        return projectAccess
    } catch (error) {
        throw new Error(error)
    }
}

export async function getUserAccounts(customerId) {
    try {
        const projectAccess = await fetchResource(`/customers/${customerId}/users`)
        return projectAccess
    } catch (error) {
        throw new Error(error)
    }
}

export async function uploadProjectLogo(projectId, file) {
    try {
        const formData = new FormData()
        const logoFile = new File([file], `logo.png`, { type: file.type })
        formData.append('file', logoFile)
        return fetchResource(`/blob/project/${projectId}/upload`, {
            method: 'POST',
            body: formData
        });
    } catch (error) {
        throw new Error(error)
    }
}

export async function getTags(projectId) {
    try {
        return await fetchResource(`/tags/${projectId}`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getUserconfig() {
    try {
        return await fetchResource(`/users/x2bconfig`)
    } catch (error) {
        throw new Error(error.status == 401 ? "TOKEN_EXPIRED" : error)
    }
}

export async function saveUserconfig(data) {
    try {
        return await fetchResource(`/users/x2bconfig`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',

            },
            body: JSON.stringify(data),
        })
    } catch (error) {
        throw new Error(error)
    }
}

export async function getProjectStats(projectId) {
    return await fetchResource(`/projects/${projectId}/stats`)
}

export async function getUserProjects(customerId) {
    try {
        return await fetchResource(`/customers/${customerId}/projectsfordashboard`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getProjectPolygons(projectId) {
    try {
        return await fetchResource(`/projects/${projectId}/polygons`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function deleteProject(projectId) {

    try {
        const data = await fetchResource(`/projects/${projectId}`, {
            method: "DELETE"
        })
        return data
    } catch (error) {
        throw new Error(error.status == 405 ? "NOT ALLOWED" : error)
    }
}

export async function getUserProjectCentriods() {
    try {
        return await fetchResource(`/projectpolygons/centroids`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getCustomerProjectCentriods(customerId) {
    try {
        return await fetchResource(`/customers/${customerId}/locations`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getCustomerDetails(customerId, startDate, endDate = undefined) {
    try {
        return await fetchResource(`/customers/${customerId}?from=${startDate}${endDate ? `&to=${endDate}` : ""}`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function getAnnotations(projectId) {
    try {
        return await fetchResource(`/annotation/projectid?id=${projectId}`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function fetchKilometerFeatures(strnr) {
    try {
        return await fetchGeoserverResource(`/d2b/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=d2b:kilometrierung_x2bim&outputFormat=application%2Fjson&srsname=EPSG:3857&FILTER=<Filter><PropertyIsEqualTo><PropertyName>st_nummer</PropertyName><Literal>${strnr}</Literal></PropertyIsEqualTo></Filter>`)
    } catch (error) {
        throw new Error(error)
    }
}

export async function fetchIvlFeatures(ivlcode) {
    try {
        return await fetchGeoserverResource(`/d2b/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=d2b%3Aivl_sections&outputFormat=application%2Fjson&srsname=EPSG:3857&FILTER=<Filter><PropertyIsEqualTo><PropertyName>ivl_abschnitt</PropertyName><Literal>${ivlcode}</Literal></PropertyIsEqualTo></Filter>`)
    } catch (error) {
        throw new Error(error)
    }
}

export function fetchAllowedConverters(layerId) {
    const data = {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/tracks/layer/${layerId}/converter`, data);
}

export function getLayersInProject(projectId, layerType) {
    const data = {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/tracks/${projectId}/layers${layerType ? `?layerType=${layerType}` : ""}`, data);
}

export function getUploadsForLayer(layerId) {
    const data = {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/tracks/layer/${layerId}/uploads`, data);
}

export function getServicesForLayer(layerId) {
    const data = {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/tracks/layer/${layerId}/services`, data);
}

export function addServiceForLayer(layerId, serviceUrl, serviceType) {
    const data = {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
        },
        body: {
            url: serviceUrl,
            serviceType: serviceType
        }
    }

    return fetchResource(`/tracks/layer/${layerId}/service`, data);
}

export async function fetchPresetForConverter(projectId, converterType, sampleFeature) {
    const data = {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
        },
        body: sampleFeature
    }

    try {
        return await fetchResource(`/tracks/${projectId}/preset/${converterType}/find`, data);
    } catch (error) {
        throw new Error(error);
    }
}

export async function getWholeTrack(layerId, lineNumber, directionIndicator) {
    const data = {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/tracks/layer/${layerId}/wholetrack?lineNumber=${lineNumber}&directionIndicator=${directionIndicator}`, data)
}

export async function getGeoServerResponse(processId) {
    return await fetchGeoserverResource(`/rest/workspaces/d2b/coveragestores/${processId}-ortho/coverages/${processId}-ortho.json`);
}

export function addNewLayer(projectId, name, color, dimensions, trackLayerTypeId, iconId, layerGroupId) {
    const data = {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
        },
        body: {
            name: name,
            color: color,
            dimensions: parseInt(dimensions),
            layerType: parseInt(trackLayerTypeId),
            trackLayerTypeId: parseInt(trackLayerTypeId),
            IconId: iconId,
            layerGroupId
        },
    }
    return fetchResource(`/tracks/${projectId}/layer`, data);
}

export function editExistingLayer(layerId, name, color, dimensions, trackLayerTypeId, iconId, layerGroupId) {

    console.log("editExistingLayer", layerId, name, color, dimensions, trackLayerTypeId, iconId, layerGroupId);
    const data = {
        method: "PUT",
        headers: {
            'Content-Type': 'application/json',
        },
        body: {
            name: name,
            color: color,
            dimensions: parseInt(dimensions),
            layerType: parseInt(trackLayerTypeId),
            trackLayerTypeId: parseInt(trackLayerTypeId),
            IconId: iconId,
            layerGroupId
        },
    }
    return fetchResource(`/tracks/layer/${layerId}`, data);
}

export function getLayerGroups(projectId) {
    const data = {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        }
    }
    return fetchResource(`/tracks/groups/${projectId}`, data);
}

export function createLayerGroup(projectId, payload) {
    const data = {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
        },
        body: payload,
    }
    return fetchResource(`/tracks/groups/${projectId}`, data);
}

export function editLayerGroup(groupId, payload) {
    const data = {
        method: "PATCH",
        headers: {
            'Content-Type': 'application/json',
        },
        body: {
            ...payload,
            layerGroupId: groupId
        },
    }
    return fetchResource(`/tracks/groups`, data);
}

export function deleteLayerGroup(groupId) {
    const data = {
        method: "DELETE",
        headers: {
            'Content-Type': 'application/json',
        }
    }
    return fetchResource(`/tracks/groups/${groupId}`, data);
}



/*
files: [fileName: string]: File
 */
export function uploadFilesToLayer(layerId, files) {
    const formData = new FormData()

    Object.entries(files).forEach(([fileName, file]) => {
        formData.append(fileName, file)
    })

    const data = {
        method: "POST",
        body: formData,
    }
    return fetchTrackConvertorResource(`/tracktopography?layerId=${layerId}`, data);
}

export function uploadToLayer(layerId, converterName, mappings, additionalPropertyKeys, featureCollection, fileName = null) {
    const data = {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
        },
        body: {
            ConverterName: converterName,
            MappingConfiguration: mappings,
            AdditionalAttributeKeys: additionalPropertyKeys,
            FeatureCollection: featureCollection,
            FileName: fileName
        },
    }

    return fetchResource(`/tracks/layer/${layerId}/upload`, data);
}

export async function deleteLayer(layerId) {
    const data = {
        method: "DELETE",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/tracks/layer/${layerId}`, data);
}

export async function deleteUpload(uploadId) {
    const data = {
        method: "DELETE",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/tracks/upload/${uploadId}`, data);
}

export async function getSpatials(layerId, extent, step, signal = undefined) {
    const data = {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        },
        ...(signal && { signal }),
    }

    let qs = ""
    if (extent) qs += `?minX=${extent[0]}&minY=${extent[1]}&maxX=${extent[2]}&maxY=${extent[3]}`
    if (step) qs += `${qs ? "&" : "?"}step=${Math.max(step, 1)}`

    return await fetchResource(`/tracks/layer/${layerId}/spatials${qs}`, data);
}

export async function getSpatialsFromGeoServer(typeName, extent) {
    const data = {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return await fetchGeoserverResource(`/d2b/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=d2b:${typeName}_x2bim&outputFormat=application%2Fjson&srsname=EPSG:3857&bbox=${extent.join(',')}`, data);
}

export async function getProjectSortings(projectId) {
    const data = {
        method: "GET",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/project/${projectId}/settings/sortings`, data);
}

export async function updateProjectSorting(projectId, resourceType, sorting) {
    const data = {
        method: "PUT",
        headers: {
            'Content-Type': 'application/json',
        },
        body: `"${sorting}"`
    }

    return fetchResource(`/project/${projectId}/settings/sortings/${resourceType}`, data);
}

export async function resetProjectSorting(projectId, resourceType) {
    const data = {
        method: "DELETE",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/project/${projectId}/settings/sortings/${resourceType}`, data);
}

export async function resetAllProjectSortings(projectId) {
    const data = {
        method: "DELETE",
        headers: {
            'Content-Type': 'application/json',
        }
    }

    return fetchResource(`/project/${projectId}/settings/sortings`, data);
}